import React, { useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TerminalIcon from '@mui/icons-material/Terminal';
import SshTerminalView, { SshConnState } from './SshTerminalView';

interface SshTerminalDialogProps {
  open: boolean;
  onClose: () => void;
  /** Public IPv4 of the reserved machine to SSH into (super-admin host path). */
  host?: string;
  /** Debug-bot id (operator path); the backend resolves the host server-side. */
  botId?: string;
  /** Label shown in the title bar (e.g. bot id or reservation id). */
  label?: string;
}

/**
 * Full in-browser SSH terminal (xterm.js) to a reserved bot VM, bridged over a
 * backend WebSocket (`/ws/ssh`). Thin dialog wrapper around {@link SshTerminalView}.
 * Provide either `host` (super-admin) or `botId` (operator debug bot).
 */
const SshTerminalDialog: React.FC<SshTerminalDialogProps> = ({ open, onClose, host, botId, label }) => {
  const [state, setState] = useState<SshConnState>('connecting');

  const statusColor =
    state === 'open' ? 'success.main' : state === 'error' ? 'error.main' : 'text.secondary';
  const statusText =
    state === 'open'
      ? 'connected'
      : state === 'connecting'
      ? 'connecting…'
      : state === 'error'
      ? 'error'
      : 'disconnected';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
        <TerminalIcon fontSize="small" />
        <Typography component="span" sx={{ fontWeight: 600 }}>
          SSH · {label || host || botId}
        </Typography>
        <Typography component="span" variant="caption" sx={{ color: statusColor, ml: 1 }}>
          {statusText}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Close">
          <IconButton
            aria-label="close terminal"
            onClick={onClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent sx={{ p: 1 }}>
        <SshTerminalView active={open} host={host} botId={botId} onStateChange={setState} />
      </DialogContent>
    </Dialog>
  );
};

export default SshTerminalDialog;
